import React, { useState, useEffect, useRef } from 'react'
import QRCode from 'react-qr-code'
import html2canvas from 'html2canvas'
import JsPDF from 'jspdf'
import { useTranslation } from 'react-i18next'

import { CROPS } from 'constants/farming'
import { makeRequest } from 'services/request.service'

import ModalComponent from 'components/common/ModalComponent'
import SelectComponent from 'components/common/SelectComponent'
import ButtonComponent from 'components/common/ButtonComponent'
import { QR_TYPES } from 'constants/qr.types'

const labelClassGroup = 'block text-sm font-medium text-gray-700'
const inputClassGroup = 'w-full p-2 border rounded-md shadow-sm'

const RowEditor = ({ title, formData, onChange, varietiesOptions = [] }) => {
  const { t } = useTranslation()
  const handleCropChange = (e) => {
    const value = e.target.value
    onChange({ ...formData, cropId: value, varietyId: '' })
  }

  const handleVarietyChange = (e) => {
    onChange({ ...formData, varietyId: e.target.value })
  }

  const handleNumberChange = (e) => {
    onChange({ ...formData, numberOfPlants: e.target.value })
  }

  const handleBarcodeChange = (e) => {
    onChange({ ...formData, barcode: e.target.value })
  }

  // filter varieties by selected crop
  const filteredVarieties = varietiesOptions.filter(
    variety => String(variety.cropId) === String(formData.cropId)
  )

  return (
    <div className='grid grid-cols-3 gap-4'>
      {title && <h3 className='col-span-3 text-md font-semibold text-green-700'>{title}</h3>}
      <div className='col-span-1'>
        <label className={labelClassGroup}>{t('mgmt.tunnelRow.numberOfPlants')}</label>
        <input
          type='number'
          name='numberOfPlants'
          value={formData.numberOfPlants || 0}
          onChange={handleNumberChange}
          className={inputClassGroup}
        />
      </div>

      <div className='col-span-1'>
        <label className={labelClassGroup}>{t('mgmt.tunnelRow.crop')}</label>
        <SelectComponent name='cropId' value={formData.cropId} onChange={handleCropChange}>
          <option value='' disabled>{t('mgmt.tunnelRow.selectCrop')}</option>
          {CROPS.map((crop, index) => (
            <option key={crop} value={index + 1}>{crop}</option>
          ))}
        </SelectComponent>
      </div>

      <div className='col-span-1'>
        <label className={labelClassGroup}>{t('mgmt.tunnelRow.variety')}</label>
        <SelectComponent name='varietyId' value={formData.varietyId} onChange={handleVarietyChange}>
          <option value='' disabled>{t('mgmt.tunnelRow.selectVariety')}</option>
          {filteredVarieties.map(variety => (
            <option key={variety.varietyId} value={variety.varietyId}>{variety.name}</option>
          ))}
        </SelectComponent>
      </div>

      <div className='col-span-3'>
        <label className={labelClassGroup}>{t('mgmt.tunnelRow.barcode')}</label>
        <input
          type='text'
          name='barcode'
          value={formData.barcode || ''}
          onChange={handleBarcodeChange}
          className={inputClassGroup}
          placeholder={t('mgmt.tunnelRow.enterBarcode')}
        />
      </div>
    </div>
  )
}

const TunnelRowModal = ({
  isOpen,
  onClose,
  rowIndex,
  varietiesOptions = [],
  onRefresh,
  initialData = {
    rowId: '',
    cropId: '',
    varietyId: '',
    numberOfPlants: 0,
    subA: null,
    subB: null
  }
}) => {
  const { t } = useTranslation()
  const qrRef = useRef(null)
  const [showQR, setShowQR] = useState(false)

  const [formDataA, setFormDataA] = useState(null)
  const [formDataB, setFormDataB] = useState(null)

  const patchMultipleRows = async (rows) => {
    return await makeRequest({
      method: 'PATCH',
      url: '/management/farming-blocks/rows',
      data: rows,
      headers: {
        Authorization: `Bearer ${localStorage.getItem('secret')}`
      }
    })
  }

  // Builds the PATCH payload for a sub-row, only sending barcode when it has a
  // value (null/undefined means "leave the stored barcode untouched"; the
  // backend schema types barcode as a string, so null must not be sent).
  const buildRowUpdate = (row) => {
    const update = {
      rowId: row.rowId,
      cropId: row.cropId,
      varietyId: row.varietyId,
      numberOfPlants: parseFloat(row.numberOfPlants) || 0
    }
    if (row.barcode !== null && row.barcode !== undefined) {
      update.barcode = String(row.barcode)
    }
    return update
  }

  const handleSave = async () => {
    const rowsToUpdate = []

    if (formDataA?.rowId) {
      rowsToUpdate.push(buildRowUpdate(formDataA))
    }

    if (formDataB?.rowId) {
      rowsToUpdate.push(buildRowUpdate(formDataB))
    }

    try {
      await patchMultipleRows(rowsToUpdate)
      onClose()
      onRefresh()
    } catch (error) {
      console.error('Failed to update rows:', error)
    }
  }

  useEffect(() => {
    if (!isOpen) {
      setShowQR(false)
    }
  }, [isOpen])

  useEffect(() => {
    if (!initialData || !initialData.rowId) return

    if (initialData.subA || initialData.subB) {
      // hydroponic row
      setFormDataA(initialData.subA || {})
      setFormDataB(initialData.subB || {})
    } else {
      // soil row
      setFormDataA(initialData)
      setFormDataB(null)
    }
  }, [initialData])

  const handlePrintQR = async (rowId) => {
    if (!qrRef.current) return

    try {
      const canvas = await html2canvas(qrRef.current)
      const imgData = canvas.toDataURL('image/png')

      const pdf = new JsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' })
      const pdfWidth = pdf.internal.pageSize.getWidth()
      const imgWidth = 100
      const imgHeight = 100
      const x = (pdfWidth - imgWidth) / 2
      const y = 40

      pdf.setFontSize(14)
      pdf.setTextColor(34, 139, 34)
      pdf.text(t('mgmt.tunnelRow.pdfTunnelRow', { n: rowIndex + 1 }), pdfWidth / 2, y - 20, { align: 'center' })

      pdf.setFontSize(12)
      pdf.setTextColor(80, 80, 80)
      pdf.text(t('mgmt.tunnelRow.pdfRowId', { id: rowId }), pdfWidth / 2, y - 10, { align: 'center' })

      pdf.addImage(imgData, 'PNG', x, y, imgWidth, imgHeight)
      pdf.save(`qr-${rowId}.pdf`)
    } catch (err) {
      console.error('Failed to print QR:', err)
    }
  }

  return (
    <ModalComponent
      title={t('mgmt.tunnelRow.updateRowTitle', { n: rowIndex + 1 })}
      initialData={initialData}
      onClose={onClose}
      isOpen={isOpen}
      hideDefaultFooter
    >
      {({ formData }) => {
        if (!formDataA) return null

        const rowEditorContent = formDataB
          ? (
            <>
              <RowEditor title='SUB_A' formData={formDataA} onChange={setFormDataA} varietiesOptions={varietiesOptions} />
              <RowEditor title='SUB_B' formData={formDataB} onChange={setFormDataB} varietiesOptions={varietiesOptions} />
            </>
            )
          : (
            <RowEditor title='' formData={formDataA} onChange={setFormDataA} varietiesOptions={varietiesOptions} />
            )

        // One QR per real sub-row so each carries its own barcode (SUB_A / SUB_B),
        // falling back to the single soil row.
        const qrRows = [formDataA, formDataB].filter((row) => row && row.rowId)

        const qrRowsContent = (showQR && qrRows.length > 0) && (
          qrRows.map(({ rowId, barcode }) => (
            <div key={rowId} className='flex flex-col items-center'>
              {barcode !== null && barcode !== undefined && barcode !== '' && (
                <span className='text-sm text-gray-600 mb-2'>{barcode}</span>
              )}
              <QRCode value={JSON.stringify({ qrType: QR_TYPES.ROW, id: rowId })} size={180} />
              <span className='text-sm text-gray-600 mt-2'>{rowId}</span>
            </div>
          ))
        )

        const customFooterButtons = formData?.rowId && (
          <>
            <ButtonComponent onClick={onClose} customColorsStyles='bg-gray-100 text-greenPrimary hover:text-white'>
              {t('mgmt.common.cancel')}
            </ButtonComponent>

            {formData?.rowId && (
              <>
                <ButtonComponent
                  onClick={() => setShowQR(prev => !prev)}
                  customColorsStyles='bg-gray-100 text-greenPrimary hover:text-white'
                >
                  {showQR ? t('mgmt.tunnelRow.hideQr') : t('mgmt.tunnelRow.showQr')}
                </ButtonComponent>

                <ButtonComponent onClick={() => handlePrintQR(formData.rowId)}>
                  {t('mgmt.qrModal.printQr')}
                </ButtonComponent>
              </>
            )}

            <ButtonComponent
              onClick={() => handleSave()}
              className='px-4 py-2 rounded-lg hover:bg-green-700'
            >
              {t('mgmt.common.save')}
            </ButtonComponent>
          </>
        )

        return (
          <div className='space-y-6 max-w-md mx-auto'>
            <div className='grid grid-cols-1 gap-4'>
              {rowEditorContent}
            </div>

            <div className='flex flex-wrap gap-4 justify-center' ref={qrRef}>
              {qrRowsContent}
            </div>

            <div className='flex justify-end space-x-4 pt-4'>
              {customFooterButtons}
            </div>
          </div>
        )
      }}
    </ModalComponent>
  )
}

export default TunnelRowModal
